import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, ArrowRight, ShieldCheck, Palette, Check, X as CloseX,
  Sparkle, Warning, ArrowClockwise, Prohibit, MedalMilitary, Plus,
} from "@phosphor-icons/react";

/**
 * StewardConsole — /steward/:code
 *
 * The private panel where a teacher, parent or school admin steward
 * manages one youth. Auth-gated at the backend level — every call to
 * `/api/youth/steward/*` or `/api/youth/{person_id}/*` requires the
 * caller to be registered as a steward of that youth.
 *
 * Sections:
 *   1. Avatar picker (saves for real, unlike the public /y/<code> page)
 *   2. Public role picker
 *   3. School visibility toggles
 *   4. Badge award grid
 *   5. Rotate Youth Code (retire the current URL, mint a new one)
 *   6. Revoke Youth Code (soft-delete — /y/<code> starts 404-ing)
 */
const BAND_LABEL = {
  under_8: "Under 8",
  "8_10":  "Age Group 8–10",
  "11_13": "Age Group 11–13",
  "14_15": "Age Group 14–15",
  "16_17": "Age Group 16–17",
  adult:   "Adult",
};

export default function StewardConsole() {
  const { code } = useParams();
  const nav = useNavigate();
  const [youth, setYouth] = useState(null);
  const [pub, setPub] = useState(null);
  const [err, setErr] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [roles, setRoles] = useState([]);
  const [badges, setBadges] = useState([]);
  const [saving, setSaving] = useState(false);

  // Bootstrap — steward-authed lookup returns the private profile.
  const load = () => {
    http.get(`/youth/steward/by-code/${encodeURIComponent(code)}`)
      .then(({ data }) => { setYouth(data.youth); setPub(data.public); })
      .catch((e) => {
        if (e?.response?.status === 401) setErr("Please sign in to open the steward console.");
        else if (e?.response?.status === 403) setErr("You are not registered as a steward for this Youth Code.");
        else setErr("Couldn't load this profile.");
      });
  };
  useEffect(() => { load(); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    http.get("/youth/catalog").then(({ data }) => {
      setRoles(data.public_roles || []);
      setBadges(data.badges || []);
    });
    http.get("/youth/avatars").then(({ data }) => setCatalog(data));
  }, []);

  const personId = youth?.id;

  const patch = async (updates, okMessage) => {
    if (!personId) return;
    setSaving(true);
    try {
      const { data } = await http.patch(`/youth/${personId}`, updates);
      setYouth(data.youth); setPub(data.public);
      toast.success(okMessage || "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const awardBadge = async (slug) => {
    if (!personId) return;
    try {
      const { data } = await http.post(`/youth/${personId}/badge`, { badge: slug });
      setPub(data.public);
      toast.success(`Awarded ${slug.replace(/_/g, " ")}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't award badge");
    }
  };

  const rotate = async () => {
    if (!personId) return;
    if (!window.confirm("Retire the current Youth Code and mint a new one? The old /y/<code> URL will start returning 404 immediately.")) return;
    try {
      const { data } = await http.post(`/youth/${personId}/rotate-code`);
      toast.success(`New Youth Code: ${data.youth_code}`);
      nav(`/steward/${data.youth_code}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rotate failed");
    }
  };

  const revoke = async () => {
    if (!personId) return;
    const reason = window.prompt("Optional reason (kept internal — not shown publicly):", "");
    if (reason === null) return;   // cancelled
    if (!window.confirm("Revoke this Youth Code? The public /y/<code> page will start 404-ing. Private history is kept for a future adult claim.")) return;
    try {
      await http.post(`/youth/${personId}/revoke`, { reason });
      toast.success("Revoked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const unrevoke = async () => {
    if (!personId) return;
    try {
      await http.post(`/youth/${personId}/unrevoke`);
      toast.success("Restored");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  if (err) {
    return (
      <main className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] pt-36 md:pt-40 pb-20 px-5" data-testid="steward-console-error">
        <div className="max-w-xl mx-auto text-center">
          <ShieldCheck size={22} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
          <h1 className="mt-3 font-serif-display text-3xl">Steward console</h1>
          <p className="mt-3 text-[#3A3830]">{err}</p>
          <Link
            to="/"
            className="mt-6 inline-flex rounded-full bg-[#1B4332] text-[#F5EBDC] px-5 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase"
          >
            Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main
      className="min-h-screen bg-[color:var(--np-bg,#F5EBDC)] pt-36 md:pt-40 pb-24 px-5 md:px-10"
      data-testid="steward-console"
    >
      <div className="max-w-3xl mx-auto">
        <Link
          to={`/y/${code}`}
          className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#1B4332]"
        >
          <ArrowLeft size={11} weight="bold" /> Public profile
        </Link>

        {/* Header */}
        <header className="mt-3 rounded-3xl border border-black/10 bg-white p-5 md:p-6 flex items-start gap-5">
          <div className="shrink-0 w-24 h-24 md:w-28 md:h-28 rounded-full overflow-hidden bg-[#F0E4CE] border border-black/10">
            {pub?.avatar?.head_url && (
              <img
                src={pub.avatar.head_url}
                alt=""
                className="w-full h-full object-cover"
                style={{ objectPosition: "50% 22%" }}
              />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              <ShieldCheck size={11} weight="fill" className="inline mr-1" /> Steward console
            </div>
            <h1
              className="mt-1 font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tight"
              data-testid="steward-console-name"
            >
              {pub?.first_name || "…"}
              {pub?.youth_code && (
                <span className="ml-3 text-[color:var(--np-brand,#B85F2A)] align-baseline text-xl md:text-2xl font-mono-tech tracking-[0.12em]">
                  {pub.youth_code}
                </span>
              )}
            </h1>
            {pub && (
              <p className="mt-1 text-sm text-[#3A3830]">
                {BAND_LABEL[pub.age_band]} · {[pub.region, pub.country].filter(Boolean).join(", ")}
                {youth?.revoked_at && (
                  <span data-testid="steward-console-revoked" className="ml-2 rounded-full bg-red-100 text-red-800 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.24em] uppercase">
                    Revoked
                  </span>
                )}
              </p>
            )}
          </div>
        </header>

        {/* Public role */}
        <Section title="Public role" testid="steward-section-role">
          <div className="mt-2 flex flex-wrap gap-2">
            {roles.map((r) => {
              const on = youth?.public_role === r;
              return (
                <button
                  key={r}
                  onClick={() => patch({ public_role: r }, `Role set to "${r}"`)}
                  data-testid={`steward-role-${r.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${
                    on
                      ? "bg-[#1B4332] text-[#F5EBDC] border-[#1B4332]"
                      : "bg-white border-black/15 hover:border-black/40"
                  }`}
                >
                  {r}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Avatar */}
        <Section title="Anonymous face" testid="steward-section-avatar">
          <p className="text-sm text-[#3A3830]">
            Picks below save immediately. No personal photo can be uploaded.
          </p>
          {catalog && (
            <AvatarGrid
              catalog={catalog}
              active={{ variant: pub?.avatar?.variant, index: pub?.avatar?.index }}
              onPick={(variant, index) => patch({ avatar_variant: variant, avatar_index: index }, "Avatar saved")}
            />
          )}
        </Section>

        {/* School visibility */}
        <Section title="School visibility" testid="steward-section-school">
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            <Toggle
              label="Show school name publicly"
              hint="Only enable if the school has opted-in."
              on={!!youth?.school_public_display}
              onChange={(v) => patch({ school_public_display: v }, "Visibility saved")}
              testid="steward-toggle-school-name"
            />
            <div className="rounded-2xl border border-black/10 bg-white p-4">
              <div className="text-xs font-mono-tech tracking-[0.28em] uppercase text-[#3A3830]">
                Public group (optional)
              </div>
              <input
                value={youth?.school_group_public || ""}
                onChange={(e) => setYouth((y) => ({ ...y, school_group_public: e.target.value }))}
                onBlur={(e) => patch({ school_group_public: e.target.value }, "Group saved")}
                placeholder="e.g. Nature Club"
                data-testid="steward-input-school-group"
                className="mt-1 w-full bg-transparent border-b border-black/15 py-1 text-sm outline-none focus:border-black/50"
              />
            </div>
          </div>
        </Section>

        {/* Badges */}
        <Section title="Award a badge" testid="steward-section-badges">
          <p className="text-sm text-[#3A3830]">
            Small green-checked badges reward specific activities.
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
            {badges.map((b) => {
              const owned = (pub?.badges || []).some((x) => x.slug === b.slug);
              return (
                <button
                  key={b.slug}
                  disabled={owned}
                  onClick={() => awardBadge(b.slug)}
                  data-testid={`steward-badge-${b.slug}`}
                  className={`flex items-center gap-2 rounded-2xl border p-3 text-left ${
                    owned
                      ? "bg-emerald-50 border-emerald-200 opacity-70"
                      : "bg-white border-black/10 hover:border-[color:var(--np-brand,#B85F2A)]"
                  }`}
                >
                  <span className="text-2xl" aria-hidden>{b.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display leading-tight truncate">{b.label}</div>
                    <div className="font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#3A3830]">
                      {owned ? "Owned" : "Award"}
                    </div>
                  </div>
                  {owned ? <Check size={14} weight="bold" className="text-emerald-700" /> : <Plus size={14} weight="bold" />}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Danger zone */}
        <Section title="Danger zone" testid="steward-section-danger" tone="danger">
          <div className="grid sm:grid-cols-2 gap-3">
            <button
              onClick={rotate}
              disabled={saving}
              data-testid="steward-rotate-btn"
              className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left hover:bg-amber-100"
            >
              <div className="flex items-center gap-2 text-amber-900">
                <ArrowClockwise size={14} weight="bold" />
                <div className="font-serif-display text-lg">Rotate Youth Code</div>
              </div>
              <p className="mt-1 text-xs text-amber-900/80">
                Retire the current <code>{pub?.youth_code}</code> URL and
                issue a fresh one. Old links stop working immediately.
              </p>
            </button>
            {!youth?.revoked_at ? (
              <button
                onClick={revoke}
                data-testid="steward-revoke-btn"
                className="rounded-2xl border border-red-200 bg-red-50 p-4 text-left hover:bg-red-100"
              >
                <div className="flex items-center gap-2 text-red-900">
                  <Prohibit size={14} weight="bold" />
                  <div className="font-serif-display text-lg">Revoke Youth Code</div>
                </div>
                <p className="mt-1 text-xs text-red-900/80">
                  Soft-delete. The public <code>/y/&lt;code&gt;</code> page
                  starts 404-ing. Private history is preserved.
                </p>
              </button>
            ) : (
              <button
                onClick={unrevoke}
                data-testid="steward-unrevoke-btn"
                className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-left hover:bg-emerald-100"
              >
                <div className="flex items-center gap-2 text-emerald-900">
                  <ShieldCheck size={14} weight="bold" />
                  <div className="font-serif-display text-lg">Restore Youth Code</div>
                </div>
                <p className="mt-1 text-xs text-emerald-900/80">
                  Reverse the revoke. Public page comes back online.
                </p>
              </button>
            )}
          </div>
        </Section>
      </div>
    </main>
  );
}

// --------------------------------------------------------------------------
//  Sub-components
// --------------------------------------------------------------------------
function Section({ title, testid, tone = "default", children }) {
  return (
    <section
      className={`mt-8 rounded-3xl border p-5 md:p-6 ${
        tone === "danger" ? "border-red-200/60 bg-red-50/40" : "border-black/10 bg-[#FBF2E1]/50"
      }`}
      data-testid={testid}
    >
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
        {title}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function Toggle({ label, hint, on, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`text-left rounded-2xl border p-4 ${
        on ? "bg-emerald-50 border-emerald-200" : "bg-white border-black/10 hover:border-black/40"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className={`inline-block w-8 h-4 rounded-full transition ${on ? "bg-emerald-500" : "bg-black/15"} relative`}>
          <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${on ? "left-4" : "left-0.5"}`} />
        </span>
        <div className="font-serif-display text-base leading-tight">{label}</div>
      </div>
      {hint && <p className="mt-1 text-xs text-[#3A3830]">{hint}</p>}
    </button>
  );
}

function AvatarGrid({ catalog, active, onPick }) {
  const groups = useMemo(() => ([
    { key: "silhouette", label: "Silhouette", items: catalog.heads || [] },
    { key: "expressive", label: "Expressive", items: catalog.expressive || [] },
    { key: "vibe",       label: "Vibe",       items: catalog.vibe || [] },
  ]), [catalog]);

  return (
    <div className="mt-3 space-y-4">
      {groups.map((g) => (
        <div key={g.key}>
          <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830]">
            {g.label}
          </div>
          <div className="mt-1 grid grid-cols-5 sm:grid-cols-10 gap-2">
            {g.items.map((it) => {
              const on = active?.variant === g.key && active?.index === it.index;
              return (
                <button
                  key={it.index}
                  onClick={() => onPick(g.key, it.index)}
                  data-testid={`steward-avatar-${g.key}-${it.index}`}
                  className={`relative aspect-square rounded-xl overflow-hidden border ${
                    on
                      ? "border-[color:var(--np-brand,#B85F2A)] ring-2 ring-[color:var(--np-brand,#B85F2A)]/40"
                      : "border-black/10 hover:border-black/40"
                  } bg-[#F0E4CE]`}
                >
                  <img
                    src={it.url}
                    alt=""
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "50% 22%" }}
                    loading="lazy"
                  />
                  {on && (
                    <span className="absolute top-1 right-1 rounded-full bg-[color:var(--np-brand,#B85F2A)] text-white p-0.5">
                      <Check size={9} weight="bold" />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
